import React, { useState, useEffect } from 'react';

export const LiveStats = ({ webSocketData, connectionStatuses, dashboardData }) => {
    const [recentActivity, setRecentActivity] = useState([]);
    const [processedActivityMessages, setProcessedActivityMessages] = useState(new Set()); // For deduplication

    // Activity message deduplication function
    const generateActivityMessageId = (message) => {
        const type = message?.type || 'unknown';
        const notificationType = message?.notification_type || 'unknown';
        const timestamp = message?.timestamp || Date.now();
        const dataString = message?.data ? JSON.stringify(message.data) : 'no-data';
        const dataPreview = dataString.substring(0, 30);

        return `${type}_${notificationType}_${timestamp}_${dataPreview}`;
    };

    const isActivityAlreadyProcessed = (messageId) => {
        if (processedActivityMessages.has(messageId)) {
            // console.log(`⚠️ LiveStats: Duplicate activity message detected and skipped: ${messageId}`);
            return true;
        }

        // Add to processed messages and clean up old ones (keep last 50)
        setProcessedActivityMessages(prev => {
            const newSet = new Set(prev);
            newSet.add(messageId);

            // Keep only the last 50 messages to prevent memory bloat
            if (newSet.size > 50) {
                const array = Array.from(newSet);
                return new Set(array.slice(-50));
            }

            return newSet;
        });

        return false;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Update recent activity when new WebSocket data arrives
    useEffect(() => {
        // Handle dashboard WebSocket data
        if (webSocketData['/ws/finance/dashboard/']) {
            const dashboardMessage = webSocketData['/ws/finance/dashboard/'];

            // Safety check for message data
            if (!dashboardMessage || typeof dashboardMessage !== 'object') {
                // console.log('LiveStats: ⚠️ Invalid dashboard message received:', dashboardMessage);
                return;
            }

            // console.log('LiveStats: Dashboard WebSocket data received:', dashboardMessage);

            // Check for duplicate messages
            const messageId = generateActivityMessageId(dashboardMessage);
            if (isActivityAlreadyProcessed(messageId)) {
                return; // Skip duplicate message
            }

            // console.log(`LiveStats: Processing unique message: ${messageId.substring(0, 50)}...`);

            // Handle dashboard updates with different notification types
            if (dashboardMessage.type === 'dashboard_update') {

                // Handle complete dashboard refresh notification
                if (dashboardMessage.notification_type === 'complete_dashboard_refresh') {
                    // console.log('LiveStats: Processing complete dashboard refresh notification');
                    const refreshData = dashboardMessage.data;

                    const newActivity = {
                        type: 'dashboard_refresh',
                        notification_type: 'complete_dashboard_refresh',
                        data: refreshData.trigger_data,
                        message: `${refreshData.trigger_reason}`,
                        timestamp: new Date(dashboardMessage.timestamp),
                        source: 'dashboard',
                        id: `refresh_${Date.now()}`
                    };

                    // console.log(newActivity);

                    setRecentActivity(prev => [
                        ...prev.slice(-9), // Keep last 9
                        newActivity
                    ]);

                    // console.log(`LiveStats: Added dashboard refresh activity for: ${refreshData.trigger_reason}`);
                }

                // Handle payment received notification
                else if (dashboardMessage.notification_type === 'payment_received') {
                    // console.log('LiveStats: Processing payment received notification');
                    const paymentData = dashboardMessage.data;

                    const newActivity = {
                        type: 'payment_received',
                        notification_type: 'payment_received',
                        data: paymentData,
                        message: `Payment received from ${paymentData.student_name}`,
                        timestamp: new Date(dashboardMessage.timestamp),
                        source: 'dashboard',
                        id: paymentData.transaction_id
                    };

                    setRecentActivity(prev => [
                        ...prev.slice(-9), // Keep last 9
                        newActivity
                    ]);

                    // console.log(`LiveStats: Added payment activity - ₹${paymentData.amount} from ${paymentData.student_name}`);
                }

                // Handle balance transfer notification
                else if (dashboardMessage.notification_type === 'balance_transferred') {
                    // console.log('LiveStats: Processing balance transfer notification');
                    const transferData = dashboardMessage.data;

                    const newActivity = {
                        type: 'transfer_completed',
                        notification_type: 'balance_transferred',
                        data: transferData,
                        message: `Balance transferred: ₹${transferData.amount}`,
                        timestamp: new Date(dashboardMessage.timestamp),
                        source: 'dashboard',
                        id: transferData.transaction_id || `transfer_${Date.now()}`
                    };

                    setRecentActivity(prev => [
                        ...prev.slice(-9), // Keep last 9
                        newActivity
                    ]);

                    // console.log(`LiveStats: Added transfer activity - ₹${transferData.amount}`);
                }

                // Handle deposit made notification
                else if (dashboardMessage.notification_type === 'deposit_made') {
                    // console.log('LiveStats: Processing deposit made notification');
                    const depositData = dashboardMessage.data;

                    const newActivity = {
                        type: 'deposit_made',
                        notification_type: 'deposit_made',
                        data: depositData,
                        message: `Deposit made: ₹${depositData.amount}`,
                        timestamp: new Date(dashboardMessage.timestamp),
                        source: 'dashboard',
                        id: depositData.transaction_id || `deposit_${Date.now()}`
                    };

                    setRecentActivity(prev => [
                        ...prev.slice(-9), // Keep last 9
                        newActivity
                    ]);

                    // console.log(`LiveStats: Added deposit activity - ₹${depositData.amount}`);
                }

                // Handle reconciliation completed notification
                else if (dashboardMessage.notification_type === 'deposit_reconciled') {
                    // console.log('LiveStats: Processing reconciliation completed notification');
                    const reconciliationData = dashboardMessage.data;

                    const newActivity = {
                        type: 'reconciliation_completed',
                        notification_type: 'deposit_reconciled',
                        data: reconciliationData,
                        message: `Deposit reconciled: ₹${reconciliationData.amount}`,
                        timestamp: new Date(dashboardMessage.timestamp),
                        source: 'dashboard',
                        id: reconciliationData.transaction_id || `reconcile_${Date.now()}`
                    };

                    setRecentActivity(prev => [
                        ...prev.slice(-9), // Keep last 9
                        newActivity
                    ]);

                    // console.log(`LiveStats: Added reconciliation activity - ₹${reconciliationData.amount}`);
                }
            }

            // Handle other dashboard message types (legacy support)
            else if (dashboardMessage.type && ['payment_received', 'transfer_completed', 'reconciliation_completed'].includes(dashboardMessage.type)) {
                setRecentActivity(prev => [
                    ...prev.slice(-9), // Keep last 9
                    {
                        ...dashboardMessage,
                        timestamp: new Date(),
                        source: 'dashboard'
                    }
                ]);
            }
        }

        // Handle notifications WebSocket data
        if (webSocketData['/ws/finance/notifications/']) {
            const notificationMessage = webSocketData['/ws/finance/notifications/'];
            // console.log('LiveStats: Notification WebSocket data received:', notificationMessage);

            if (notificationMessage.type && ['payment_received', 'transfer_completed', 'reconciliation_completed'].includes(notificationMessage.type)) {
                setRecentActivity(prev => [
                    ...prev.slice(-9), // Keep last 9
                    {
                        ...notificationMessage,
                        timestamp: new Date(),
                        source: 'notifications'
                    }
                ]);
            }
        }
    }, [webSocketData]);

    const getActivityIcon = (type) => {
        switch (type) {
            case 'payment_received': return '💰';
            case 'transfer_completed': return '🔄';
            case 'reconciliation_completed': return '✅';
            case 'dashboard_refresh': return '🔄';
            case 'deposit_made': return '🏦';
            default: return '📝';
        }
    };

    const getActivityColor = (type) => {
        switch (type) {
            case 'payment_received': return 'success';
            case 'transfer_completed': return 'info';
            case 'reconciliation_completed': return 'warning';
            case 'dashboard_refresh': return 'info';
            case 'deposit_made': return 'success';
            default: return 'default';
        }
    };

    return (
        <div className="live-stats-sidebar">
            {/* Connection Status */}
            {/* <div className="stats-section">
                <h3>Live Connection</h3>
                <div className={`connection-indicator ${isAnyConnected ? 'connected' : 'disconnected'}`}>
                    <span className="status-dot"></span>
                    <span className="status-text">
                        {getOverallStatus()}
                    </span>
                </div>
                {isAnyConnected && (
                    <small className="connection-info">Real-time updates active</small>
                )}
                <div className="endpoint-status">
                    <small>Dashboard: {connectionStatuses['/ws/finance/dashboard/'] || 'disconnected'}</small>
                    <small>Notifications: {connectionStatuses['/ws/finance/notifications/'] || 'disconnected'}</small>
                </div>
            </div> */}

            {/* Recent Activity Feed */}
            <div className="stats-section">
                <h3>Recent Activity</h3>
                <div className="activity-feed">
                    {recentActivity.length === 0 ? (
                        <div className="no-activity">
                            <p>No recent activity</p>
                            <small>Live updates will appear here</small>
                        </div>
                    ) : (
                        recentActivity.map((activity, index) => (
                            <div
                                key={index}
                                className={`activity-item ${getActivityColor(activity.type)}`}
                            >
                                <div className="activity-icon">
                                    {getActivityIcon(activity.type)}
                                </div>
                                <div className="activity-content">
                                    <div className="activity-message">
                                        {activity.message}
                                    </div>
                                    <div className="activity-details">
                                        {/* Payment Received Details */}
                                        {activity.type === 'payment_received' && activity.data && (
                                            <>
                                                <span className="amount">
                                                    {formatCurrency(activity.data.amount)}
                                                </span>
                                                <span className="student-name">
                                                    Student: {activity.data.student_name}
                                                </span>
                                                <span className="payment-method">
                                                    via {activity.data.payment_method}
                                                </span>
                                                <span className="received-by">
                                                    Received by: {activity.data.received_by}
                                                </span>
                                                <span className="transaction-id">
                                                    ID: {activity.data.transaction_id}
                                                </span>
                                            </>
                                        )}

                                        {/* Balance Transfer Details */}
                                        {activity.type === 'transfer_completed' && activity.data && (
                                            <>
                                                <span className="amount">
                                                    {formatCurrency(activity.data.amount)}
                                                </span>
                                                <span className="instrument">
                                                    Instrument: {activity.data.instrument}
                                                </span>
                                                <span className="transfer-from">
                                                    From: {activity.data.from_user}
                                                </span>
                                                <span className="transfer-to">
                                                    To: {activity.data.to_user}
                                                </span>
                                                <span className={`verification-status ${activity.data.verified ? 'verified' : 'pending'}`}>
                                                    {activity.data.verified ? '✅ Verified' : '⏳ Pending Verification'}
                                                </span>
                                                <span className="transfer-id">
                                                    ID: {activity.data.transfer_id}
                                                </span>
                                            </>
                                        )}

                                        {/* Deposit Made Details */}
                                        {activity.type === 'deposit_made' && activity.data && (
                                            <>
                                                <span className="amount">
                                                    {formatCurrency(activity.data.amount)}
                                                </span>
                                                <span className="instrument">
                                                    Instrument: {activity.data.instrument}
                                                </span>
                                                <span className="deposited-by">
                                                    Deposited by: {activity.data.deposited_by}
                                                </span>
                                                <span className="bank-account">
                                                    Bank: {activity.data.bank_account}
                                                </span>
                                                <span className="deposit-id">
                                                    ID: {activity.data.deposit_id}
                                                </span>
                                            </>
                                        )}

                                        {/* Deposit Reconciled Details */}
                                        {activity.type === 'reconciliation_completed' && activity.data && (
                                            <>
                                                <span className="amount">
                                                    {formatCurrency(activity.data.amount)}
                                                </span>
                                                <span className="reconciliation-status">
                                                    Status: {activity.data.status}
                                                </span>
                                                <span className="reconciled-by">
                                                    Reconciled by: {activity.data.reconciled_by}
                                                </span>
                                                <span className="deposit-reference">
                                                    Deposit ID: {activity.data.deposit_id}
                                                </span>
                                                <span className="reconciliation-id">
                                                    ID: {activity.data.reconciliation_id}
                                                </span>
                                            </>
                                        )}

                                        {/* Dashboard Refresh Details */}
                                        {activity.type === 'dashboard_refresh' && activity.data && (
                                            <>
                                                {activity.data.amount && (
                                                    <span className="amount">
                                                        {formatCurrency(activity.data.amount)}
                                                    </span>
                                                )}
                                                {activity.data.student_name && (
                                                    <span className="student-name">
                                                        Student: {activity.data.student_name}
                                                    </span>
                                                )}
                                                {activity.data.payment_method && (
                                                    <span className="payment-method">
                                                        via {activity.data.payment_method}
                                                    </span>
                                                )}
                                                {activity.data.from_user && (
                                                    <span className="transfer-users">
                                                        {activity.data.from_user} → {activity.data.to_user}
                                                    </span>
                                                )}
                                                {activity.data.deposited_by && (
                                                    <span className="deposited-by">
                                                        by {activity.data.deposited_by}
                                                    </span>
                                                )}
                                                {activity.data.reconciled_by && (
                                                    <span className="reconciled-by">
                                                        by {activity.data.reconciled_by}
                                                    </span>
                                                )}
                                            </>
                                        )}

                                        {/* Fallback for legacy data format */}
                                        {!['payment_received', 'transfer_completed', 'deposit_made', 'reconciliation_completed', 'dashboard_refresh'].includes(activity.type) && activity.data?.amount && (
                                            <span className="amount">
                                                {formatCurrency(activity.data.amount)}
                                            </span>
                                        )}
                                        {!['payment_received', 'transfer_completed', 'deposit_made', 'reconciliation_completed', 'dashboard_refresh'].includes(activity.type) && activity.data?.employee && (
                                            <span className="employee">
                                                by {activity.data.employee}
                                            </span>
                                        )}
                                    </div>
                                    <div className="activity-time">
                                        {formatTime(activity.timestamp)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* System Status */}
            {/* <div className="stats-section">
                <h3>System Status</h3>
                <div className="system-status">
                    <div className="status-item">
                        <span className="status-indicator good"></span>
                        <span>API Connection</span>
                    </div>
                    <div className="status-item">
                        <span className={`status-indicator ${isAnyConnected ? 'good' : 'error'}`}></span>
                        <span>WebSocket</span>
                    </div>
                    <div className="status-item">
                        <span className="status-indicator good"></span>
                        <span>Data Sync</span>
                    </div>
                </div>
                <div className="last-update">
                    <small>
                        Last update: {new Date().toLocaleTimeString('en-IN')}
                    </small>
                </div>
            </div> */}
        </div>
    );
};
